import { useState, useMemo, useEffect } from 'react';
import { Save, RotateCcw } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { generateId } from '../../utils/helpers';
import DateInput from '../../components/common/DateInput';

const subjectPill = [
  'bg-blue-100 text-blue-700', 'bg-green-100 text-green-700', 'bg-purple-100 text-purple-700',
  'bg-orange-100 text-orange-700', 'bg-pink-100 text-pink-700', 'bg-teal-100 text-teal-700',
  'bg-yellow-100 text-yellow-800', 'bg-red-100 text-red-700',
];

export default function AddHomework() {
  const { homework, classes, subjects, setNotifications, saveHomework, teacherMapping, loadTeacherMapping } = useApp();
  const { currentUser } = useAuth();

  useEffect(() => {
    if (currentUser?.role === 'teacher') loadTeacherMapping();
  }, [currentUser, loadTeacherMapping]);
  const today = new Date().toISOString().split('T')[0];

  const isAdmin        = currentUser?.role === 'admin';
  const isClassTeacher = currentUser?.role === 'teacher' && teacherMapping.isClassTeacher;
  const myPairs        = teacherMapping.classSubjectPairs || [];

  // Classes this teacher can assign homework to — union of their own
  // homeroom (if a class teacher) and any class they're a subject teacher
  // in, not either/or: a class teacher of one class who also teaches a
  // subject in another still needs both to show up here.
  const assignableClasses = isAdmin
    ? classes
    : classes.filter(c =>
        (isClassTeacher && c.id === teacherMapping.classId) ||
        teacherMapping.assignedClassIds.includes(c.id));

  const [classId, setClassId]   = useState(assignableClasses[0]?.id || '');
  const [dueDate, setDueDate]   = useState('');
  const [rows, setRows]         = useState({});
  const [saved, setSaved]       = useState(false);

  const classSubjects = useMemo(() => {
    const forClass = subjects.filter(s => (s.classIds || []).includes(classId));
    const mapped = forClass.length > 0 ? forClass : subjects;
    // The "every subject" bypass only applies to their OWN homeroom class —
    // not globally whenever isClassTeacher is true, or a class teacher of
    // one class would wrongly get every subject in any other class they
    // teach too, once that class became selectable via the fix above.
    if (isAdmin || (isClassTeacher && classId === teacherMapping.classId)) return mapped;
    // Subject teacher: only subjects assigned to them in this specific class
    const pairedSubjectIds = new Set(
      myPairs.filter(p => p.classId === classId).map(p => p.subjectId)
    );
    return mapped.filter(s => pairedSubjectIds.has(s.id));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjects, classId, teacherMapping]);

  const setField = (subjectId, field, value) =>
    setRows(prev => ({ ...prev, [subjectId]: { ...prev[subjectId], [field]: value } }));

  const reset = () => { setRows({}); setDueDate(''); setSaved(false); };

  const filledCount = classSubjects.filter(s => rows[s.id]?.title?.trim()).length;

  const save = async () => {
    const cls = classes.find(c => c.id === classId);
    const newItems = [];
    const newNotifs = [];
    classSubjects.forEach(sub => {
      const row = rows[sub.id];
      if (!row?.title?.trim()) return;
      const hwId = generateId('HW');
      newItems.push({
        id: hwId, title: row.title.trim(), description: row.description || '',
        classId, subjectId: sub.id, dueDate,
        assignedDate: today, status: 'active', assignedBy: currentUser?.profileId || 'STF-001',
      });
      newNotifs.push({
        id: generateId('NTF'), type: 'homework',
        title: `New Homework: ${row.title.trim()}`,
        body: `${cls ? `${cls.name}-${cls.section}` : ''} | ${sub.name} | Due: ${dueDate}`,
        classId, homeworkId: hwId,
        createdAt: new Date().toISOString(), targetRoles: ['parent'], readBy: [],
      });
    });
    if (!newItems.length) return;
    for (const item of newItems) { await saveHomework(item); }
    setNotifications(prev => [...newNotifs, ...prev]);
    setSaved(true);
    setRows({});
    setDueDate('');
  };

  // Count today's homework for selected class
  const todayCount = homework.filter(h => h.classId === classId && h.assignedDate === today).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Add Homework</h1>
          <p className="text-gray-500 text-sm">Assign homework to all subjects at once</p>
        </div>
        {todayCount > 0 && (
          <span className="bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1.5 rounded-lg font-medium">
            {todayCount} already assigned today for this class
          </span>
        )}
      </div>

      {saved && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-1 rounded-lg text-sm font-medium flex items-center gap-2">
          ✓ Homework saved successfully! Fill in the form again to assign more.
        </div>
      )}

      {/* Class + Date row */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold mb-1.5">Class & Section</label>
            <select value={classId} onChange={e => { setClassId(e.target.value); setRows({}); setSaved(false); }}
              className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
              {assignableClasses.map(c => <option key={c.id} value={c.id}>{c.name} – Section {c.section}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1.5">Due Date</label>
            <DateInput value={dueDate} min={today} onChange={e => setDueDate(e.target.value)}
              className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
        </div>
      </div>

      {/* Subject rows */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-5">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <span className="text-sm font-semibold">Subjects ({classSubjects.length})</span>
          <span className="">{filledCount} filled · leave blank to skip</span>
        </div>
        <table className="w-full text-sm">
          <thead className="border-b border-gray-100">
            <tr className="uppercase">
              <th className="text-left px-5 py-2.5 font-semibold w-36">Subject</th>
              <th className="text-left px-3 py-1 font-semibold">Homework Title</th>
              <th className="text-left px-3 py-1 font-semibold">Description / Instructions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {classSubjects.map((sub, idx) => {
              const filled = !!rows[sub.id]?.title?.trim();
              return (
                <tr key={sub.id} className={filled ? 'bg-green-50/40' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                  <td className="px-5 py-3">
                    <span className={`inline-block font-bold px-2.5 py-1 rounded-full ${subjectPill[idx % subjectPill.length]}`}>
                      {sub.name}
                    </span>
                    <div className="text-[10px] mt-0.5 pl-0.5">{sub.code}</div>
                  </td>
                  <td className="px-3 py-3">
                    <input type="text"
                      placeholder={`e.g. Chapter ${idx + 3} exercise…`}
                      value={rows[sub.id]?.title || ''}
                      onChange={e => setField(sub.id, 'title', e.target.value)}
                      className="w-full px-3 py-1 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white" />
                  </td>
                  <td className="px-3 py-3">
                    <input type="text"
                      placeholder="Optional details…"
                      value={rows[sub.id]?.description || ''}
                      onChange={e => setField(sub.id, 'description', e.target.value)}
                      className="w-full px-3 py-1 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <button onClick={reset} className="flex items-center gap-2 px-4 py-1 text-sm hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors">
          <RotateCcw size={14} /> Clear All
        </button>
        <button onClick={save}
          disabled={!dueDate || filledCount === 0}
          className="flex items-center gap-2 px-6 py-2.5 text-sm text-white rounded-lg font-semibold disabled:opacity-50 transition-colors"
          style={{ backgroundColor: '#6b21a8' }}>
          <Save size={15} /> Save {filledCount > 0 ? `(${filledCount} subject${filledCount > 1 ? 's' : ''})` : ''}
        </button>
      </div>
    </div>
  );
}
